import { useEffect, useRef, useState } from 'react';
import { ACCESS_TOKEN_KEY, PARKING_PROXY_URL } from '../../config';

export const POLL_MS = 10000;
const KEEP_MS = 72 * 3600e3;

function token() {
    try {
        return JSON.parse(localStorage.getItem(ACCESS_TOKEN_KEY));
    } catch (e) {
        return null;
    }
}

// GET from the parking proxy's live view API with the backoffice login
export async function proxyFetch(path) {
    const res = await fetch(`${PARKING_PROXY_URL}/beweb/api${path}`, {
        headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) {
        const err = new Error(
            res.status === 401 ? 'Nincs jogosultság (admin belépés kell)' : `HTTP ${res.status}`
        );
        err.status = res.status;
        throw err;
    }
    return res;
}

/**
 * All parking system events of the last 72h (entries/exits, payments, rejections, errors, device and operator
 * actions), refreshed every 10s (only the new / updated ones are transferred). Keyed by the event key:
 * an event may come again later with more details (amount, operator) and a different id.
 * @returns {{events: Map, fresh: Set, status: object|null, error: Error|null, updatedAt: Date|null}}
 */
export default function useLiveEvents() {
    const [state, setState] = useState({
        events: new Map(),
        fresh: new Set(),
        status: null,
        error: null,
        updatedAt: null,
    });
    const seq = useRef(0);

    useEffect(() => {
        let stop = false;
        let timer = null;
        const poll = async () => {
            try {
                const res = await (await proxyFetch(`/events?after=${seq.current}`)).json();
                if (stop) return;
                const firstLoad = seq.current === 0;
                setState((prev) => {
                    const events = new Map(prev.events);
                    const fresh = new Set();
                    res.events.forEach((e) => {
                        const k = e.k || e.id;
                        if (!firstLoad && !events.has(k)) fresh.add(k);
                        events.set(k, { ...e, k });
                    });
                    const min = res.now - KEEP_MS;
                    events.forEach((e, k) => e.ts < min && events.delete(k));
                    return {
                        events,
                        fresh,
                        status: res.status,
                        error: null,
                        updatedAt: new Date(),
                    };
                });
                seq.current = res.seq;
            } catch (e) {
                if (!stop) setState((prev) => ({ ...prev, error: e }));
            }
            if (!stop) timer = setTimeout(poll, POLL_MS);
        };
        poll();
        return () => {
            stop = true;
            clearTimeout(timer);
        };
    }, []);

    return state;
}
